/**
 * BulkAuditAssignModal.jsx
 *
 * ─ Single API call on modal open (locations + existing config fetched together)
 * ─ Levels are unlimited — KPIs are grouped by "next available level"
 *   (computed client-side after open), and that grouping can go beyond 3
 * ─ Each level group exposes two links into KpiAssignedAuditsDrawer:
 *     • "Unassigned KPIs"  → read-only list of KPIs still needing this level
 *     • "Assignments"      → review/remove for levels already filled below this one
 * ─ Same auditor cannot appear at two levels for the same KPI + location
 * ─ Sequential levels only (L1 before L2, L2 before L3, …)
 */

import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { apiCall } from "../../../../../../_services/apiCall";
import config from "../../../../../../config/config";
import { useCurrentUser } from "../../../../../../hooks/useCurrentUser";
import AuditorAssignedKpisDrawer from "./AuditorAssignedKpisDrawer";
import { slotAuditorIds } from "./auditorSlots";

/** Normalize a role name: lowercase + strip all whitespace */
const normalizeRoleName = (name) => (name || "").toLowerCase().replace(/\s+/g, "");

/**
 * Find a role in the list whose name matches targetKey (case & space insensitive).
 * targetKey: "internalauditor" | "externalauditor"
 */
const resolveRoleId = (roles, targetKey) => {
  const match = (roles || []).find((r) => normalizeRoleName(r.role_name) === targetKey);
  return match ? String(match.id) : null;
};


/* ════════════════════════════════════════════════
   DESIGN TOKENS
════════════════════════════════════════════════ */
const T = {
  blue: "#2563eb", blueL: "#eff6ff", blueB: "#bfdbfe",
  purple: "#7c3aed", purpleL: "#faf5ff", purpleB: "#ddd6fe",
  green: "#15803d", greenL: "#f0fdf4", greenB: "#bbf7d0",
  amber: "#b45309", amberL: "#fffbeb", amberB: "#fde68a",
  red: "#be123c", redL: "#fff1f2", redB: "#fecdd3",
  ink: "#0f172a", ink2: "#334155", ink3: "#64748b", ink4: "#94a3b8",
  rule: "#e2e8f0", rule2: "#f1f5f9", bg: "#f8fafc",
  white: "#ffffff",
};

/* ════════════════════════════════════════════════
   ATOMS
════════════════════════════════════════════════ */
const Spin = ({ size = 13, color = T.blue }) => (
  <span style={{ display: "inline-block", width: size, height: size, flexShrink: 0, border: `2px solid ${color}22`, borderTopColor: color, borderRadius: "50%", animation: "bam-spin .6s linear infinite" }} />
);

const StepBadge = ({ n, done, active }) => (
  <div style={{ width: 22, height: 22, borderRadius: "50%", flexShrink: 0, background: done ? T.green : active ? T.blue : T.rule, color: T.white, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9.5, fontWeight: 800, transition: "background .2s" }}>
    {done ? "✓" : n}
  </div>
);

const StepRow = ({ n, label, done, active }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
    <StepBadge n={n} done={done} active={active} />
    <span style={{ fontSize: 12.5, fontWeight: 700, color: done || active ? T.ink : T.ink4 }}>{label}</span>
  </div>
);

/* ════════════════════════════════════════════════
   LOCATION SINGLE-SELECT
════════════════════════════════════════════════ */
const LocSelect = ({ locations, selectedId, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const selectedLoc = locations.find((l) => l.id === selectedId);
  const label = disabled ? "Select auditor type first"
    : selectedLoc ? selectedLoc.unitCode
      : "Choose a location…";

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        style={{ width: "100%", padding: "9px 12px", fontSize: 13, cursor: disabled ? "not-allowed" : "pointer", border: `1.5px solid ${selectedId ? T.blue : T.rule}`, borderRadius: 8, background: disabled ? T.bg : selectedId ? T.blueL : T.white, color: selectedId ? T.blue : T.ink3, display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: selectedId ? 600 : 400, opacity: disabled ? 0.55 : 1 }}>
        <span>{label}</span>
        <span style={{ fontSize: 9, opacity: 0.5 }}>▾</span>
      </button>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 2000, background: T.white, border: `1px solid ${T.rule}`, borderRadius: 10, boxShadow: "0 12px 32px rgba(0,0,0,.13)", maxHeight: 230, overflowY: "auto", padding: "4px 0" }}>
          {locations.map((loc) => {
            const ch = loc.id === selectedId;
            return (
              <label key={loc.id} onClick={() => { onChange(loc.id); setOpen(false); }} style={{ display: "flex", gap: 8, padding: "7px 13px", cursor: "pointer", fontSize: 12, background: ch ? T.blueL : "transparent", alignItems: "center" }}>
                <span style={{ fontWeight: 600, color: T.ink2 }}>{loc.unitCode}</span>
                {loc.city && <span style={{ color: T.ink4, fontSize: 11 }}>{loc.city}</span>}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ════════════════════════════════════════════════
   AUDITOR MULTI-SELECT (external levels — several auditors share a level)
════════════════════════════════════════════════ */
/**
 * Multi-auditor picker for an external level. Options render in-flow (not
 * absolutely positioned) so the enclosing card grows and the modal body
 * scrolls — an absolute menu would be clipped by the card's overflow:hidden.
 */
const AuditorMultiSelect = ({ options, selectedIds, onChange, disabled, color, light, border }) => {
  const [open, setOpen] = useState(false);

  const selectedSet = new Set((selectedIds || []).map(String));
  const toggle = (id) => {
    const next = selectedSet.has(String(id))
      ? selectedIds.filter((x) => String(x) !== String(id))
      : [...selectedIds, String(id)];
    onChange(next);
  };

  const selectedNames = options.filter((o) => selectedSet.has(String(o.id))).map((o) => o.name);
  const label = !options.length ? "No eligible auditors"
    : selectedNames.length === 0 ? "— Select auditors —"
      : selectedNames.length <= 2 ? selectedNames.join(", ")
        : `${selectedNames.length} auditors selected`;
  const filled = (selectedIds || []).length > 0;

  return (
    <div style={{ flex: 1 }}>
      <button type="button"
        onClick={() => !disabled && options.length && setOpen((o) => !o)}
        style={{ width: "100%", padding: "7px 10px", border: `1.5px solid ${filled ? border : T.rule}`, borderRadius: 7, fontSize: 12.5, background: filled ? light : T.white, color: filled ? color : T.ink2, cursor: (disabled || !options.length) ? "not-allowed" : "pointer", fontWeight: filled ? 600 : 400, outline: "none", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6, textAlign: "left" }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
        <span style={{ fontSize: 9, opacity: 0.5 }}>{open ? "▴" : "▾"}</span>
      </button>
      {open && options.length > 0 && (
        <div style={{ marginTop: 5, background: T.white, border: `1px solid ${T.rule}`, borderRadius: 8, maxHeight: 180, overflowY: "auto", padding: "3px 0" }}>
          {options.map((u) => {
            const ch = selectedSet.has(String(u.id));
            return (
              <label key={u.id} onClick={() => toggle(u.id)} style={{ display: "flex", gap: 8, padding: "7px 11px", cursor: "pointer", fontSize: 12, background: ch ? light : "transparent", alignItems: "center" }}>
                <input type="checkbox" readOnly checked={ch} style={{ accentColor: color, flexShrink: 0 }} />
                <span style={{ fontWeight: ch ? 700 : 500, color: ch ? color : T.ink2 }}>{u.name}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ════════════════════════════════════════════════
   MAIN MODAL
════════════════════════════════════════════════ */
// When `tabular` is set, the modal runs in company-wide mode:
//  • The location step is hidden.
//  • Auditors aren't filtered by location.
//  • `locationIds` is omitted from getExistingAssignmentConfig + assignAuditorsToQuestions.
// To keep the downstream code (which loops over `selectedLocIds`) unchanged,
// we pin `selectedLocIds` to a single sentinel value `__tabular__` and
// normalize the API's `groupedTabular` response into `{ __tabular__: ... }`.
const TABULAR_LOC_SENTINEL = '__tabular__';
const BulkAuditAssignModal = ({ 
  show, 
  onHide, 
  selectedIds = [], 
  financialYearId, 
  managementListValue = [], 
  reportingQuestions,
  onSuccess, 
  tabular = false 
}) => {
  const isMounted = useRef(true);
  const { companyId } = useCurrentUser();
  useEffect(() => () => { isMounted.current = false; }, []);

  /* ── All data state ── */
  const [allLocations, setAllLocations] = useState([]);
  const [allAuditors, setAllAuditors] = useState([]);
  const [allRoles, setAllRoles] = useState([]);  // from getMasterData — full role list
  const [existingGrouped, setExistingGrouped] = useState(null);
  const [questionTitles, setQuestionTitles] = useState({});
  const [loadingInit, setLoadingInit] = useState(false);
  const [loadingAuditors, setLoadingAuditors] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(false);

  /* ── Form state ── */
  const [auditorType, setAuditorType] = useState("");
  const [selectedLocIds, setSelectedLocIds] = useState([]);
  const [levelGroups, setLevelGroups] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  /* ── Drawer state ── */
  const [drawer, setDrawer] = useState(null); // { qIds, label, mode, level }

  /**
   * Turns a scope id from the picker (`12` or `sub_5`) into the
   * {locationId, subLocationId} pair the audit APIs expect.
   *
   * A `sub_<id>` key names no parent site of its own — it is resolvable only
   * through `allLocations`, which loads asynchronously. Returns `null` while
   * unresolved (`Number("sub_5")` is NaN) so callers skip the scope instead of
   * posting a NaN location id.
   *
   * @param {string|number} scopeId Option id from the location picker.
   * @returns {{locationId: number, subLocationId: number|null}|null} The scope,
   *   or null when it cannot be resolved to a real location.
   */
  const scopeOf = useCallback((scopeId) => {
    const match = allLocations.find((l) => String(l.id) === String(scopeId));
    if (match) return { locationId: match.locationId, subLocationId: match.subLocationId ?? null };
    const locationId = Number(scopeId);
    return Number.isFinite(locationId) ? { locationId, subLocationId: null } : null;
  }, [allLocations]);

  /**
   * Resolves picked scope ids for a request payload.
   * @param {Array<string|number>} scopeIds Option ids from the location picker.
   * @returns {{scopes: Array<{locationId: number, subLocationId: number|null}>, unresolved: boolean}}
   *   `unresolved` is true when any id could not be resolved, in which case the
   *   caller must not send the request.
   */
  const resolveScopes = useCallback((scopeIds) => {
    const scopes = scopeIds.map(scopeOf).filter(Boolean);
    return { scopes, unresolved: scopes.length !== scopeIds.length };
  }, [scopeOf]);

  /* ──────────────────────────────────────────────────────────
     SINGLE API CALL ON OPEN
  ────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!show) return;

    setAllRoles([]);
    setAuditorType(""); setSelectedLocIds(tabular ? [TABULAR_LOC_SENTINEL] : []); setLevelGroups([]);
    setAllAuditors([]); setExistingGrouped(null);
    setError(""); setSuccess(""); setDrawer(null);

    setLoadingInit(true);
    setLoadingRoles(true);

    /* Fetch locations and all roles in parallel */
    Promise.all([
      apiCall(`${config.POSTLOGIN_API_URL_COMPANY}getSource`, {}, {}, "GET"),
      apiCall(`${config.POSTLOGIN_API_URL_COMPANY}authorization/authroles`, {}, {}, "GET"),
    ]).then(([locRes, rolesRes]) => {
      if (!isMounted.current) return;
      if (locRes.isSuccess) {
        // One option per auditable scope. A site split into sub-locations is
        // represented only by them (KMC-T1, KMC-T2) — auditors are assigned per
        // sub-location, never to KMC itself.
        const scopeOptions = [];
        (locRes.data?.data || []).forEach((l) => {
          const parentCode = l.unitCode || `Loc ${l.id}`;
          const hasSubLocations = l.hasSubLocations ?? ((l.subLocation?.length ?? 0) > 0);

          if (!hasSubLocations) {
            scopeOptions.push({
              id: String(l.id),
              locationId: Number(l.id),
              subLocationId: null,
              unitCode: parentCode,
              city: l.location?.city || "",
            });
          }

          (l.subLocation || []).forEach((sub) => {
            scopeOptions.push({
              id: `sub_${sub.id}`,
              locationId: Number(l.id),
              subLocationId: Number(sub.id),
              unitCode: sub.displayName || `${parentCode}-${sub.subLocation}`,
              city: l.location?.city || "",
            });
          });
        });
        setAllLocations(scopeOptions);
      }
      if (rolesRes.isSuccess) {
        const rawRoles = Array.isArray(rolesRes.data) ? rolesRes.data : (rolesRes.data?.data || []);
        setAllRoles(rawRoles);
        /* Auto-select default auditor type based on which roles exist by name */
        const internalExists = rawRoles.some((r) => normalizeRoleName(r.role_name) === "internalauditor");
        const externalExists = rawRoles.some((r) => normalizeRoleName(r.role_name) === "externalauditor");
        if (internalExists && !externalExists) setAuditorType("INTERNAL");
        else if (!internalExists && externalExists) setAuditorType("EXTERNAL");
        // else leave blank so user picks
      }
    }).finally(() => {
      if (isMounted.current) { setLoadingInit(false); setLoadingRoles(false); }
    });
  }, [show]); // eslint-disable-line


  /* ── Fetch existing config ──
     Quantitative: gated on selectedLocIds (need locations chosen first).
     Tabular     : selectedLocIds is pinned to [TABULAR_LOC_SENTINEL] on open,
                   so the gate passes; we omit locationIds from the request
                   and pivot `groupedTabular` into the sentinel key so all
                   downstream loops can stay unchanged.
     Scopes only resolve once `allLocations` has loaded, so an unresolved set
     skips the request entirely — querying with half of it would return the
     wrong existing config. `resolveScopes` sits in the deps, so the effect
     refires and fetches for real as soon as the locations arrive.        */
  const fetchExistingConfig = useCallback(() => {
    if (!selectedLocIds.length || !selectedIds.length) {
      setExistingGrouped(null);
      return;
    }
    const { scopes, unresolved } = resolveScopes(tabular ? [] : selectedLocIds);
    if (unresolved) return;
    setLoadingInit(true);
    return apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}getExistingAssignmentConfig`,
      {},
      {
        questionIds: selectedIds,
        ...(tabular ? {} : { scopes }),
        financialYearId,
        auditorType: auditorType || "",
      },
      "POST",
      false,
      false
    ).then((existRes) => {
      if (existRes.isSuccess && isMounted.current) {
        const payload = existRes.data?.data;
        if (tabular) {
          setExistingGrouped({ [TABULAR_LOC_SENTINEL]: payload?.groupedTabular || {} });
        } else {
          setExistingGrouped(payload?.grouped || {});
        }
        if (payload?.questionTitles) setQuestionTitles(payload.questionTitles);
      }
    }).finally(() => { if (isMounted.current) setLoadingInit(false); });
  }, [selectedLocIds, selectedIds, auditorType, tabular, financialYearId, resolveScopes]);

  useEffect(() => {
    if (!show) { setExistingGrouped(null); return; }
    fetchExistingConfig();
  }, [show, fetchExistingConfig]);

  /* ── When auditor type changes → resolve role by name → fetch users ── */
  useEffect(() => {
    if (!auditorType) return;
    if (!allRoles.length) return; // wait until roles are loaded
    setAllAuditors([]); setSelectedLocIds(tabular ? [TABULAR_LOC_SENTINEL] : []); setLevelGroups([]);
    setLoadingAuditors(true);

    const targetKey = auditorType === "INTERNAL" ? "internalauditor" : "externalauditor";
    const roleId = resolveRoleId(allRoles, targetKey);
    if (!roleId) {
      setAllAuditors([]); setSelectedLocIds(tabular ? [TABULAR_LOC_SENTINEL] : []); setLevelGroups([]);
      setLoadingAuditors(false);
      return;
    }

    apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}getSubUserBasedOnRoleId`, {},
      { roleId }, "GET"
    ).then(({ isSuccess, data }) => {
      if (isSuccess && isMounted.current) {
        setAllAuditors((data?.data || []).map((u) => ({
          id: String(u.userId),
          name: [u.firstName, u.lastName].filter(Boolean).join(" ") + (u.designation ? ` (${u.designation})` : ""),
          locationIds: (u.location || []).map((l) => l.sourceId),
        })));
      }
    }).finally(() => { if (isMounted.current) setLoadingAuditors(false); });
  }, [auditorType, allRoles]);

  /** Max level currently assigned for a (qid, locId) pair. 0 = nothing assigned yet.
   *  This is now the single source of truth every group and every downstream
   *  view derives from — no separate "next level" helper needed. */
  const getMaxAssignedLevel = useCallback((qid, lid) => {
    const locData = existingGrouped?.[String(lid)] || {};
    let max = 0;
    Object.entries(locData).forEach(([lv, slot]) => {
      if ((slot.questionIds || []).map(Number).includes(Number(qid))) {
        max = Math.max(max, Number(lv));
      }
    });
    return max;
  }, [existingGrouped]);

  useEffect(() => {
    if (!selectedLocIds.length || !existingGrouped) { setLevelGroups([]); return; }

    const maxLevelByQidLoc = {};
    let highestMax = 0;
    selectedIds.forEach((qid) => {
      maxLevelByQidLoc[qid] = {};
      selectedLocIds.forEach((lid) => {
        const m = getMaxAssignedLevel(qid, lid);
        maxLevelByQidLoc[qid][lid] = m;
        highestMax = Math.max(highestMax, m);
      });
    });

    // Given a predicate on maxLevel, collect matching (qid -> locIds[]) pairs.
    const pairsWhere = (predicate) => {
      const byQid = new Map();
      selectedIds.forEach((qid) => {
        const locIds = selectedLocIds.filter((lid) => predicate(maxLevelByQidLoc[qid][lid]));
        if (locIds.length) byQid.set(qid, locIds);
      });
      return [...byQid.entries()].map(([qid, locIds]) => ({ qid, locIds }));
    };

    const groups = [];
    for (let level = 1; level <= highestMax + 1; level++) {
      const entries = pairsWhere((max) => max === level - 1);   // needs this level next
      const filledEntries = pairsWhere((max) => max === level); // this IS their max — nothing higher exists

      // Keep every level in the 1..highestMax+1 sequence visible, even one
      // where no current pair happens to need or hold it — e.g. one KPI+loc
      // pair already jumped ahead to Level 3 while everything else is still
      // sitting at Level 1, leaving Level 2 momentarily empty. Hiding it would
      // make the level sequence look discontinuous (L1 → L3, no L2).

      groups.push({
        level,
        entries,
        qIds: entries.map((e) => e.qid),
        filledEntries,
        filledCount: filledEntries.reduce((n, e) => n + e.locIds.length, 0),
        auditorIds: [],
        overwrite: false,
      });
    }

    setLevelGroups(groups);
  }, [selectedLocIds, existingGrouped, selectedIds, getMaxAssignedLevel]);

  /* ── Auditors common to ALL selected locations ──
     Tabular mode is company-wide — all auditors for the role are eligible
     regardless of which locations they manage. Quantitative still narrows
     to auditors present at every selected location.                       */
  const commonAuditors = useMemo(() => {
    if (tabular) return allAuditors;
    if (!selectedLocIds.length) return allAuditors;
    // Auditors carry parent location ids, so a sub-location scope resolves to
    // its site before the membership test.
    return allAuditors.filter((u) => selectedLocIds.every((lid) => u.locationIds.includes(scopeOf(lid)?.locationId)));
  }, [allAuditors, selectedLocIds, tabular, scopeOf]);

  const updateGroupAuditor = (level, patch) =>
    setLevelGroups((prev) => prev.map((g) => (g.level !== level ? g : { ...g, ...patch })));

  /** Local mirror of getAssignedLevels, scoped to this modal. */
  const assignedLevelsFor = useCallback((qid, lid) => {
    const locData = existingGrouped?.[String(lid)] || {};
    return Object.entries(locData)
      .filter(([, slot]) => (slot.questionIds || []).map(Number).includes(Number(qid)))
      .map(([lv, slot]) => ({ level: Number(lv), auditorIds: slotAuditorIds(slot) }));
  }, [existingGrouped]);

  /** KPIs a level card can assign auditors to. INTERNAL is single-auditor, so
   *  only its still-unassigned KPIs (`entries`) are targets. EXTERNAL is
   *  multi-auditor (OR review), so already-assigned KPIs at this level
   *  (`filledEntries`) stay targets too — you can keep adding auditors. */
  const assignTargetsFor = useCallback(
    (group) => (auditorType === "EXTERNAL" ? [...group.entries, ...group.filledEntries] : group.entries),
    [auditorType],
  );

  /** Auditors eligible for a level card: present at every location the group
   *  touches, AND not already assigned to any (qid, locId) pair in this group
   *  at any level. For Level 2 this naturally means "not in Level 1"; for an
   *  external level it means "not already one of this level's auditors". */
  const availableAuditorsForGroup = useCallback((group) => {
    const targets = assignTargetsFor(group);
    if (!targets.length) return [];
    const groupLocIds = tabular ? selectedLocIds : [...new Set(targets.flatMap((e) => e.locIds))];
    const locEligible = tabular
      ? allAuditors
      : allAuditors.filter((u) => groupLocIds.every((lid) => u.locationIds.includes(scopeOf(lid)?.locationId)));

    const excluded = new Set();
    targets.forEach(({ qid, locIds }) => {
      locIds.forEach((lid) => {
        assignedLevelsFor(qid, lid).forEach(({ auditorIds }) => auditorIds.forEach((id) => excluded.add(String(id))));
      });
    });

    return locEligible.filter((u) => !excluded.has(u.id));
  }, [allAuditors, selectedLocIds, tabular, assignedLevelsFor, scopeOf, assignTargetsFor]);

  const assignableGroups = levelGroups.filter((g) => g.entries.length > 0);
  // Only the first pending level (normally Level 1) is mandatory to enable Save —
  // higher levels can be left for a later pass. handleSave already skips any
  // group without an auditorId, so a partial save here is safe.
  const firstAssignableLevel = assignableGroups.length
    ? Math.min(...assignableGroups.map((g) => g.level))
    : null;
  const allFilled = assignableGroups.length > 0
    && (assignableGroups.find((g) => g.level === firstAssignableLevel)?.auditorIds?.length ?? 0) > 0;
  const hasPicks = levelGroups.some((g) => g.auditorIds.length > 0 && assignTargetsFor(g).length > 0);
  const canSave = allFilled || hasPicks;
  const allAssigned = !loadingInit && selectedLocIds.length > 0 && levelGroups.length === 0 && !!existingGrouped;

  // Re-derive the open drawer's entries/qIds from the *current* levelGroups on every
  // render, instead of a snapshot captured at click-time — levelGroups already recomputes
  // reactively off existingGrouped, so this keeps the drawer's KPI/user list in sync after
  // a removal inside it triggers onRefreshAssignments (fetchExistingConfig).
  const drawerGroup = drawer ? levelGroups.find((g) => g.level === drawer.level) : null;
  const drawerEntries = drawer?.mode === "unassigned" ? (drawerGroup?.entries || []) : (drawerGroup?.filledEntries || []);
  const drawerQIds = drawer?.mode === "unassigned" ? (drawerGroup?.qIds || []) : (drawerGroup?.filledEntries || []).map((e) => e.qid);

  /**
   * Cross-group guard: the same auditor picked for two levels touching the same
   * (qid, locId). Runs over every group's actual save targets — including an
   * external level being topped up (zero `entries`, only `filledEntries`) — so
   * the inline warning fires before the backend rejects the request.
   */
  const groupErrors = useMemo(() => {
    const errors = {};
    const usedBy = new Map(); // "qid-locId" -> { level, auditorIds } that claimed it
    levelGroups.forEach((g) => {
      if (!g.auditorIds.length) return;
      assignTargetsFor(g).forEach(({ qid, locIds }) => {
        locIds.forEach((lid) => {
          const pairKey = `${qid}-${lid}`;
          const claim = usedBy.get(pairKey);
          if (claim && claim.level !== g.level && claim.auditorIds.some((id) => g.auditorIds.includes(id))) {
            errors[g.level] = `An auditor is also selected at Level ${claim.level} for an overlapping KPI/location.`;
          }
          usedBy.set(pairKey, { level: g.level, auditorIds: g.auditorIds });
        });
      });
    });
    return errors;
  }, [levelGroups, assignTargetsFor]);

  const hasErrors = Object.keys(groupErrors).length > 0;

  /* ── Save ──
     An unresolved scope aborts instead of being skipped: silently dropping it
     would report success for an assignment that never reached the server. The
     per-request check stops the loop before it fires a bad request, even if
     earlier level groups already saved.                                    */
  const handleSave = async () => {
    setError(""); setSuccess("");
    if (!auditorType) { setError("Select an auditor type."); return; }
    if (!tabular && !selectedLocIds.length) { setError("Select a location."); return; }
    if (!canSave) {
      setError(firstAssignableLevel ? `Select an auditor for Level ${firstAssignableLevel}.` : "Select at least one auditor to save.");
      return;
    }
    const isExternal = auditorType === "EXTERNAL";
    if (hasErrors) { setError(Object.values(groupErrors)[0]); return; }
    if (!tabular && resolveScopes(selectedLocIds).unresolved) {
      setError("Locations are still loading. Please try again in a moment.");
      return;
    }

    setSaving(true);
    try {
      for (const group of levelGroups) {
        if (!group.auditorIds.length) continue;
        const targets = assignTargetsFor(group);
        if (!targets.length) continue;

        const bySignature = new Map();
        targets.forEach(({ qid, locIds }) => {
          const sig = tabular ? "__tabular__" : [...locIds].sort().join(",");
          if (!bySignature.has(sig)) bySignature.set(sig, { locIds, qIds: [] });
          bySignature.get(sig).qIds.push(qid);
        });

        for (const { locIds, qIds } of bySignature.values()) {
          const { scopes, unresolved } = resolveScopes(tabular ? [] : locIds);
          if (unresolved) {
            setError("Some selected locations could not be resolved. Please reopen and try again.");
            return;
          }
          const levelEntry = isExternal
            ? { level: group.level, auditorIds: group.auditorIds.map(Number), overwrite: group.overwrite }
            : { level: group.level, auditorId: Number(group.auditorIds[0]), overwrite: group.overwrite };
          await apiCall(
            `${config.POSTLOGIN_API_URL_COMPANY}assignAuditorsToQuestions`, {},
            {
              financialYearId, companyId,
              questionIds: qIds,
              ...(tabular ? {} : { scopes }),
              ...(isExternal ? { externalLevels: [levelEntry] } : { internalLevels: [levelEntry] }),
            },
            "POST"
          );
        }
      }
      setSuccess(`✓ Auditors assigned for ${selectedIds.length} KPI${selectedIds.length !== 1 ? "s" : ""}.`);
      setTimeout(() => { onSuccess?.(); onHide(); }, 1500);
    } catch (e) { setError(e?.message || "Server error."); }
    finally { if (isMounted.current) setSaving(false); }
  };

  const hasInternal = allRoles.length === 0
    ? true  // still loading, show both
    : allRoles.some((r) => normalizeRoleName(r.role_name) === "internalauditor");
  const hasExternal = allRoles.length === 0
    ? true
    : allRoles.some((r) => normalizeRoleName(r.role_name) === "externalauditor");



  if (!show) return null;

  const step1Done = !!auditorType;
  const step2Done = selectedLocIds.length > 0;
  const typeColor = auditorType === "EXTERNAL" ? T.purple : T.blue;
  const typeLight = auditorType === "EXTERNAL" ? T.purpleL : T.blueL;
  const typeBorder = auditorType === "EXTERNAL" ? T.purpleB : T.blueB;

  return (
    <>
      <style>{`@keyframes bam-spin { to { transform: rotate(360deg); } }`}</style>

      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.48)", backdropFilter: "blur(4px)", zIndex: 1200, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
        <div style={{ background: T.white, borderRadius: 16, width: "100%", maxWidth: 540, maxHeight: "94vh", display: "flex", flexDirection: "column", boxShadow: "0 24px 64px rgba(0,0,0,.22)" }}>

          {/* ── Header ── */}
          <div style={{ padding: "16px 20px 14px", borderBottom: `1px solid ${T.rule}`, display: "flex", alignItems: "center", gap: 11, flexShrink: 0 }}>
            <div style={{ width: 40, height: 40, borderRadius: 11, background: T.blueL, border: `1.5px solid ${T.blueB}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>🔍</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: T.ink, letterSpacing: "-0.01em" }}>Assign Auditors</div>
              <div style={{ fontSize: 11.5, color: T.ink3, marginTop: 2, display: "flex", alignItems: "center", gap: 5 }}>
                <span>{selectedIds.length} KPI{selectedIds.length !== 1 ? "s" : ""} selected</span>
                {step1Done && <><span style={{ color: T.rule }}>·</span><span style={{ color: typeColor, fontWeight: 600 }}>{auditorType === "INTERNAL" ? "Internal" : "External"}</span></>}
                {step2Done && !tabular && <><span style={{ color: T.rule }}>·</span><span>{allLocations.find((l) => l.id === selectedLocIds[0])?.unitCode || "1 location"}</span></>}
                {step2Done && tabular && <><span style={{ color: T.rule }}>·</span><span>company-wide</span></>}
                {loadingInit && <><span style={{ color: T.rule }}>·</span><Spin size={10} /></>}
              </div>
            </div>
            <button onClick={onHide} disabled={saving} style={{ border: `1px solid ${T.rule}`, borderRadius: 8, background: T.bg, width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 16, color: T.ink3 }}>×</button>
          </div>

          {/* ── Body ── */}
          <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>

            {error && <div style={{ padding: "9px 13px", borderRadius: 8, background: T.redL, border: `1px solid ${T.redB}`, color: T.red, fontSize: 12.5, marginBottom: 14, display: "flex", gap: 8 }}><span>⚠️</span>{error}</div>}
            {success && <div style={{ padding: "9px 13px", borderRadius: 8, background: T.greenL, border: `1px solid ${T.greenB}`, color: T.green, fontSize: 12.5, marginBottom: 14, display: "flex", gap: 8 }}><span>✅</span>{success}</div>}

            {/* ══ STEP 1: Auditor Type ══ */}
            <StepRow n={1} label="Select Auditor Type" done={step1Done} active={!step1Done} />
            <div style={{ marginBottom: 22 }}>
              <select
                value={auditorType}
                onChange={(e) => setAuditorType(e.target.value)}
                style={{ width: "100%", padding: "10px 12px", fontSize: 13, cursor: "pointer", border: `1.5px solid ${step1Done ? typeColor : T.rule}`, borderRadius: 9, background: step1Done ? typeLight : T.white, color: step1Done ? typeColor : T.ink2, fontWeight: step1Done ? 700 : 400, appearance: "auto", outline: "none" }}
              >
                <option value="">— Select auditor type —</option>
                {hasInternal && <option value="INTERNAL">Internal Auditor</option>}
                {hasExternal && <option value="EXTERNAL">External Auditor (runs after all internal levels)</option>}
              </select>

              {step1Done && (
                <div style={{ marginTop: 8, padding: "7px 11px", borderRadius: 7, background: typeLight, border: `1px solid ${typeBorder}`, fontSize: 11.5, color: typeColor, display: "flex", alignItems: "center", gap: 7 }}>
                  {loadingAuditors
                    ? <><Spin size={11} color={typeColor} /> Loading auditors…</>
                    : <><span style={{ width: 7, height: 7, borderRadius: "50%", background: typeColor, display: "inline-block", flexShrink: 0 }} /><b>{allAuditors.length}</b> {auditorType.toLowerCase()} auditor{allAuditors.length !== 1 ? "s" : ""} found</>
                  }
                </div>
              )}
            </div>

            {/* ══ STEP 2: Location ══ */}
            {!tabular && <StepRow n={2} label="Select Location" done={step2Done} active={step1Done && !step2Done} />}
            <div style={{ marginBottom: 18, opacity: step1Done ? 1 : 0.45, pointerEvents: step1Done ? "auto" : "none", display: tabular ? "none" : undefined }}>
              <div style={{ fontSize: 11.5, color: T.ink3, marginBottom: 7 }}>
                Assign one location at a time. Repeat for other locations as needed.
              </div>
              <LocSelect locations={allLocations} selectedId={selectedLocIds[0]} onChange={(id) => setSelectedLocIds([id])} disabled={!step1Done} />

              {step2Done && !tabular && (
                <div style={{ marginTop: 9, padding: "7px 11px", borderRadius: 7, fontSize: 12, fontWeight: 600, background: commonAuditors.length ? T.greenL : T.amberL, border: `1px solid ${commonAuditors.length ? T.greenB : T.amberB}`, color: commonAuditors.length ? T.green : T.amber, display: "flex", alignItems: "center", gap: 7 }}>
                  {loadingAuditors
                    ? <><Spin size={11} color={T.green} /> Loading…</>
                    : commonAuditors.length
                      ? <>✓ {commonAuditors.length} auditor{commonAuditors.length !== 1 ? "s" : ""} available at this location</>
                      : <>⚠ No auditors from this role at this location</>
                  }
                </div>
              )}
            </div>

            {/* ══ STEP 3: Level Groups ══ */}
            {!step1Done ? (
              <div style={{ padding: "11px 14px", borderRadius: 8, background: T.bg, border: `1px solid ${T.rule}`, fontSize: 12, color: T.ink3 }}>
                Select an auditor type to view and configure assignments.
              </div>
            ) : (loadingAuditors || loadingInit) ? (
              <div style={{ padding: "11px 14px", borderRadius: 8, background: T.bg, border: `1px solid ${T.rule}`, fontSize: 12, color: T.ink3, display: "flex", alignItems: "center", gap: 7 }}>
                <Spin size={11} color={T.ink3} /> Loading assignments…
              </div>
            ) : step2Done ? (
              <>
                <StepRow n={3} label="Configure Levels" done={allFilled && !hasErrors} active={step2Done && levelGroups.length > 0} />

                {/* All assigned */}
                {allAssigned && (
                  <div style={{ padding: "11px 14px", borderRadius: 8, background: T.greenL, border: `1px solid ${T.greenB}`, fontSize: 12.5, color: T.green }}>
                    ✓ All selected KPIs already have every level configured at these locations.
                  </div>
                )}

                {/* No groups yet */}
                {!loadingInit && levelGroups.length === 0 && !allAssigned && (
                  <div style={{ padding: "11px 14px", borderRadius: 8, background: T.bg, border: `1px solid ${T.rule}`, fontSize: 12, color: T.ink3 }}>
                    KPI groups will appear here once locations are selected.
                  </div>
                )}

                {/* Render groups */}
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  {levelGroups.map((group) => {
                    const available = availableAuditorsForGroup(group);
                    const err = groupErrors[group.level];
                    const showPicker = assignTargetsFor(group).length > 0
                      && (available.length > 0 || group.entries.length > 0);

                    return (
                      <div key={group.level} style={{ border: `1.5px solid ${T.rule}`, borderRadius: 12, overflow: "hidden", background: T.white }}>

                        {/* Group header — unchanged from last round */}
                        <div style={{ padding: "10px 14px 9px", background: T.bg, borderBottom: `1px solid ${T.rule}`, display: "flex", alignItems: "flex-start", gap: 10 }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                              <span style={{ padding: "2px 9px", borderRadius: 20, background: typeLight, color: typeColor, border: `1px solid ${typeBorder}`, fontSize: 11.5, fontWeight: 700 }}>
                                Level {group.level}
                              </span>
                              <span style={{ color: T.ink4, fontSize: 12 }}>{group.qIds.length} KPI{group.qIds.length !== 1 ? "s" : ""} unassigned</span>
                            </div>
                            <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
                              {group.entries.length > 0 ? (
                                <button onClick={() => setDrawer({ level: group.level, mode: "unassigned", label: `Level ${group.level} · Unassigned KPIs` })}
                                  style={{ fontSize: 11, fontWeight: 700, color: T.ink2, background: T.white, border: `1px solid ${T.rule}`, borderRadius: 20, padding: "4px 11px", cursor: "pointer", whiteSpace: "nowrap" }}>
                                  Unassigned KPIs ({group.qIds.length}) →
                                </button>
                              ) : (
                                <span style={{ fontSize: 11, fontWeight: 700, color: T.ink4, background: T.bg, border: `1px solid ${T.rule}`, borderRadius: 20, padding: "4px 11px", whiteSpace: "nowrap" }}>
                                  Unassigned KPIs (0)
                                </span>
                              )}
                              {group.filledCount > 0 && (
                                <button onClick={() => setDrawer({ level: group.level, mode: "assignments", label: `Level ${group.level} · Assignments` })}
                                  style={{ fontSize: 11, fontWeight: 600, color: T.blue, background: T.blueL, border: `1px solid ${T.blueB}`, borderRadius: 20, padding: "4px 11px", cursor: "pointer", whiteSpace: "nowrap" }}>
                                  Assignments ({group.filledCount}) →
                                </button>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Auditor selector — external levels stay open for more auditors */}
                        {showPicker ? (
                          <div style={{ padding: "10px 14px", display: "flex", flexDirection: "column", gap: 6 }}>
                            <div style={{ padding: "9px 11px", borderRadius: 8, background: err ? T.redL : "#fcfcfd", border: `1.5px solid ${err ? T.redB : T.rule}`, display: "flex", alignItems: "center", gap: 8 }}>
                              <div style={{ minWidth: 36, height: 36, borderRadius: "50%", background: typeLight, color: typeColor, border: `2px solid ${typeColor}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, flexShrink: 0 }}>
                                L{group.level}
                              </div>
                              {auditorType === "EXTERNAL" ? (
                                <AuditorMultiSelect
                                  options={available}
                                  selectedIds={group.auditorIds}
                                  onChange={(ids) => updateGroupAuditor(group.level, { auditorIds: ids })}
                                  disabled={!available.length}
                                  color={typeColor} light={typeLight} border={typeBorder}
                                />
                              ) : (
                                <select
                                  value={group.auditorIds[0] || ""}
                                  onChange={(e) => updateGroupAuditor(group.level, { auditorIds: e.target.value ? [e.target.value] : [] })}
                                  disabled={!available.length}
                                  style={{ flex: 1, padding: "7px 10px", border: `1.5px solid ${group.auditorIds.length ? typeBorder : T.rule}`, borderRadius: 7, fontSize: 12.5, background: group.auditorIds.length ? typeLight : T.white, color: group.auditorIds.length ? typeColor : T.ink2, cursor: available.length ? "pointer" : "not-allowed", fontWeight: group.auditorIds.length ? 600 : 400, outline: "none" }}
                                >
                                  <option value="">{!available.length ? "No eligible auditors" : "— Select auditor —"}</option>
                                  {available.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                                </select>
                              )}
                            </div>
                            {err && <div style={{ fontSize: 11, color: T.red, paddingLeft: 4, display: "flex", gap: 5 }}><span>⚠</span><span>{err}</span></div>}
                          </div>
                        ) : group.filledCount > 0 ? (
                          <div style={{ padding: "10px 14px", fontSize: 11.5, color: T.green, display: "flex", alignItems: "center", gap: 6 }}>
                            ✓ Fully assigned at this level — nothing to configure here.
                          </div>
                        ) : (
                          <div style={{ padding: "10px 14px", fontSize: 11.5, color: T.ink4, display: "flex", alignItems: "center", gap: 6 }}>
                            — No KPIs currently need Level {group.level}.
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            ) : null}

            {/* Legend */}
            <div style={{ marginTop: 16, padding: "9px 12px", borderRadius: 8, background: T.bg, border: `1px solid ${T.rule}`, fontSize: 10.5, color: T.ink4, lineHeight: 1.9 }}>
              <div>• <b>Unassigned KPIs</b> browses the KPIs waiting on this level — read-only, filterable by KPI and location.</div>
              <div>• <b>Assignments</b> reviews what's already assigned below this level, with the option to remove a level.</div>
              <div>• Same auditor <b>cannot appear at two levels</b> for the same KPI and location.</div>
              <div>• Levels are <b>sequential and unlimited</b> — each level requires the one before it to already exist.</div>
            </div>
          </div>

          {/* ── Footer ── */}
          <div style={{ padding: "12px 20px", borderTop: `1px solid ${T.rule}`, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between", background: T.bg, borderRadius: "0 0 16px 16px" }}>
            <div style={{ fontSize: 11.5, color: hasErrors ? T.red : T.ink3 }}>
              {hasErrors ? `${Object.keys(groupErrors).length} validation error${Object.keys(groupErrors).length !== 1 ? "s" : ""}`
                : allFilled ? `${assignableGroups.filter((g) => g.auditorIds.length).length} of ${assignableGroups.length} level${assignableGroups.length !== 1 ? "s" : ""} filled`
                  : hasPicks ? `${levelGroups.filter((g) => g.auditorIds.length).length} level${levelGroups.filter((g) => g.auditorIds.length).length !== 1 ? "s" : ""} ready to save`
                    : ""}
            </div>
            <div style={{ display: "flex", gap: 9 }}>
              <button onClick={onHide} disabled={saving} style={{ padding: "8px 18px", borderRadius: 8, border: `1.5px solid ${T.rule}`, background: T.white, color: T.ink2, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
              <button
                onClick={handleSave}
                disabled={saving || !step2Done || !canSave || hasErrors}
                style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: (saving || !step2Done || !canSave || hasErrors) ? "#93c5fd" : T.blue, color: T.white, fontSize: 13, fontWeight: 700, cursor: (saving || !step2Done || !canSave || hasErrors) ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 7 }}
              >
                {saving ? <><Spin size={12} color={T.white} /> Saving…</> : "Save Assignments"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── KPI Detail Drawer ── */}
      {drawer && (
        <AuditorAssignedKpisDrawer
          qIds={drawerQIds}
          entries={drawerEntries}
          groupLabel={drawer.label}
          mode={drawer.mode}
          levelContext={drawer.level}
          questionTitles={questionTitles}
          existingGrouped={existingGrouped}
          allLocations={allLocations}
          allAuditors={allAuditors}
          selectedLocIds={selectedLocIds}
          tabular={tabular}
          onClose={() => setDrawer(null)}
          financialYearId={financialYearId}
          auditorType={auditorType}
          reportingQuestions={reportingQuestions}
          onRefreshAssignments={fetchExistingConfig}
        />
      )}
    </>
  );
};

export default BulkAuditAssignModal;